const FADE_DURATION = 1.5; // seconds

interface Indicator {
  el: HTMLElement;
  opacity: number;
}

type Direction = 'top' | 'bottom' | 'left' | 'right';

const DIRECTIONS: Direction[] = ['top', 'bottom', 'left', 'right'];

function createIndicatorEl(dir: Direction): HTMLElement {
  const el = document.createElement('div');
  const shared =
    'position: fixed; pointer-events: none; opacity: 0; z-index: 35; transition: none; border-radius: 4px;';

  switch (dir) {
    case 'top':
      el.style.cssText = shared +
        'top: 20px; left: 50%; transform: translateX(-50%); width: min(200px, 40vw); height: 40px; ' +
        'background: linear-gradient(to bottom, rgba(255,0,0,0.6), transparent);';
      break;
    case 'bottom':
      el.style.cssText = shared +
        'bottom: 20px; left: 50%; transform: translateX(-50%); width: min(200px, 40vw); height: 40px; ' +
        'background: linear-gradient(to top, rgba(255,0,0,0.6), transparent);';
      break;
    case 'left':
      el.style.cssText = shared +
        'left: 20px; top: 50%; transform: translateY(-50%); width: 40px; height: min(200px, 40vw); ' +
        'background: linear-gradient(to right, rgba(255,0,0,0.6), transparent);';
      break;
    case 'right':
      el.style.cssText = shared +
        'right: 20px; top: 50%; transform: translateY(-50%); width: 40px; height: min(200px, 40vw); ' +
        'background: linear-gradient(to left, rgba(255,0,0,0.6), transparent);';
      break;
  }

  return el;
}

export class DirectionalIndicator {
  private container: HTMLElement;
  private indicators: Record<Direction, Indicator>;

  constructor() {
    this.container = document.createElement('div');
    this.container.style.pointerEvents = 'none';
    document.body.appendChild(this.container);

    this.indicators = {} as Record<Direction, Indicator>;
    for (const dir of DIRECTIONS) {
      const el = createIndicatorEl(dir);
      this.container.appendChild(el);
      this.indicators[dir] = { el, opacity: 0 };
    }
  }

  /**
   * Show damage from a direction (radians relative to player).
   * 0 = front (top), PI = behind (bottom), PI/2 = right, -PI/2 = left.
   */
  showDamage(direction: number): void {
    // Normalize to [-PI, PI]
    let angle = ((direction % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2) - Math.PI;

    let dir: Direction;
    const absAngle = Math.abs(angle);

    if (absAngle <= Math.PI / 4) {
      dir = 'top'; // front
    } else if (absAngle >= (Math.PI * 3) / 4) {
      dir = 'bottom'; // behind
    } else if (angle > 0) {
      dir = 'right';
    } else {
      dir = 'left';
    }

    this.indicators[dir].opacity = 1;
    this.indicators[dir].el.style.opacity = '1';
  }

  update(dt: number): void {
    const fadeRate = 1 / FADE_DURATION;
    for (const dir of DIRECTIONS) {
      const ind = this.indicators[dir];
      if (ind.opacity > 0) {
        ind.opacity = Math.max(0, ind.opacity - fadeRate * dt);
        ind.el.style.opacity = String(ind.opacity);
      }
    }
  }

  dispose(): void {
    if (this.container.parentNode) {
      this.container.remove();
    }
  }
}
